import LiquidText, { liquidTone } from '../components/LiquidText';
import { LiquidDialog } from '../components/LiquidUI';
import DeleteIcon from '../components/DeleteIcon';
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';

/* ============================================================
   EPIs (SGI — ISO 45001, 8.1)
   Catálogo de EPIs (13 do termo de posse) + registo de
   atribuição por trabalhador (tamanho, quantidade, datas).
   ============================================================ */

const fmtData = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleDateString('pt-PT'); } catch { return '—'; } };
const diaIso = (d) => (d ? String(d).substring(0, 10) : '');

/* A ficha assinada é sempre a mesma em toda a aplicação: sem `ate`, contém
   apenas a distribuição mais recente; com `ate`, contém exatamente a
   distribuição assinada nessa data. */
const descarregarFicha = async (trabalhadorId, nome, ate) => {
    const r = await api.get(`/trabalhadores/epi-document/${trabalhadorId}`, {
        responseType: 'blob', timeout: 60000, params: ate ? { ate } : {},
    });
    const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `EPI_${String(nome || 'trabalhador').trim().replace(/\s+/g, '_')}${ate ? `_${ate.split('-').reverse().join('-')}` : ''}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => window.URL.revokeObjectURL(url), 60000);
};

/* ---------- Registo de EPIs de um trabalhador ---------- */
function TrabalhadorModal({ trabalhador, catalogo, onClose, onChanged }) {
    const [atrib, setAtrib] = useState(null);
    // Entrega: data comum + os EPIs escolhidos (um, alguns ou todos), cada um
    // com o seu tamanho e quantidade.
    const [data, setData] = useState(new Date().toISOString().substring(0, 10));
    const [sel, setSel] = useState({});   // epi_id -> { tamanho, quantidade }
    const [obs, setObs] = useState('');
    const [aEntregar, setAEntregar] = useState(false);
    const [err, setErr] = useState('');
    const [entregasAbertas, setEntregasAbertas] = useState({});

    const alternarEntrega = (dia) => {
        setEntregasAbertas(estado => ({ ...estado, [dia]: !estado[dia] }));
    };

    const carregar = useCallback(async () => {
        try { const r = await api.get('/sistema-integrado/epi-atribuicoes', { params: { trabalhador_id: trabalhador.trabalhador_id } }); setAtrib(r.data || []); }
        catch { setAtrib([]); }
    }, [trabalhador.trabalhador_id]);
    useEffect(() => { carregar(); }, [carregar]);

    const remover = async (a) => {
        if (!window.confirm('Remover esta entrega do registo?')) return;
        try { await api.delete(`/sistema-integrado/epi-atribuicoes/${a.id}`); carregar(); onChanged(); }
        catch { alert('Erro ao remover'); }
    };

    const jaAtribuidos = new Set((atrib || []).map(a => a.epi_id));
    const disponiveis = catalogo.filter(c => c.ativo !== false);
    const emFalta = disponiveis.filter(c => !jaAtribuidos.has(c.id));
    const nSel = Object.keys(sel).length;

    const alternar = (id) => setSel(s => {
        if (s[id]) { const n = { ...s }; delete n[id]; return n; }
        return { ...s, [id]: { tamanho: '', quantidade: 1 } };
    });
    const definir = (id, campo, valor) => setSel(s => ({ ...s, [id]: { ...s[id], [campo]: valor } }));
    const marcarEmFalta = () => setSel(Object.fromEntries(emFalta.map(c => [c.id, { tamanho: '', quantidade: 1 }])));

    const entregar = async () => {
        if (nSel === 0) { setErr('Escolha os EPIs a entregar.'); return; }
        setAEntregar(true); setErr('');
        try {
            const itens = Object.entries(sel).map(([epi_id, v]) => ({ epi_id, tamanho: v.tamanho, quantidade: v.quantidade }));
            const r = await api.post('/sistema-integrado/epi-atribuicoes/lote', {
                trabalhador_id: trabalhador.trabalhador_id, data_entrega: data, observacoes: obs, itens,
            });
            setSel({}); setObs('');
            carregar(); onChanged();
            setErr(`✓ ${r.data.entregues} EPI(s) registado(s) na entrega de ${new Date(data).toLocaleDateString('pt-PT')}.`);
        } catch (e) { setErr(e.response?.data?.error || 'Erro ao registar a entrega.'); }
        finally { setAEntregar(false); }
    };

    // Distribuição mais recente ou a distribuição assinada numa data específica
    const ficha = async (ate) => {
        try { await descarregarFicha(trabalhador.trabalhador_id, trabalhador.nome, ate); }
        catch { setErr('Não foi possível gerar a ficha.'); }
    };

    // Entregas agrupadas pelo dia em que foram feitas, da mais recente para a mais antiga
    const entregas = Object.values((atrib || []).reduce((acc, a) => {
        const dia = diaIso(a.data_entrega) || 'sem-data';
        (acc[dia] = acc[dia] || { dia, itens: [] }).itens.push(a);
        return acc;
    }, {})).sort((x, y) => (y.dia > x.dia ? 1 : -1));

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 720 }} onClick={e => e.stopPropagation()}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <h3 style={{ margin: 0 }}>EPIs — {trabalhador.nome}</h3>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>
                <p style={{ color: '#64748b', fontSize: '0.85rem', marginTop: 0 }}>
                    Registo de posse de EPIs — mesma fonte da ficha do trabalhador: ao registar aqui, o campo "EPIs entregues" e a data do trabalhador ficam atualizados,
                    e a ficha de posse (PDF) passa a mostrar estas entregas, cada uma na sua linha, com data e assinatura.
                </p>

                {/* Entregas feitas, por data, com a ficha assinada de cada uma */}
                {atrib === null ? <p style={{ color: '#64748b' }}>A carregar…</p> : atrib.length === 0 ? (
                    <p style={{ color: '#94a3b8', fontSize: '0.88rem' }}>Sem EPIs registados para este trabalhador.</p>
                ) : (
                    <div style={{ marginBottom: 14 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8, marginBottom: 6 }}>
                            <strong style={{ color: '#334155' }}>Entregas ({atrib.length} EPI{atrib.length === 1 ? '' : 's'} em {entregas.length} entrega{entregas.length === 1 ? '' : 's'})</strong>
                            <button data-liquid-secondary="true" onClick={() => ficha()} style={{ ...btnSec, padding: '5px 12px', fontSize: '0.8rem' }}
                                title="Ficha da distribuição mais recente"><LiquidText>⬇ Última entrega (PDF)</LiquidText></button>
                        </div>
                        {entregas.map(g => {
                            const aberta = !!entregasAbertas[g.dia];
                            const titulo = g.dia === 'sem-data' ? 'Entrega sem data' : `Entrega de ${fmtData(g.dia)}`;
                            return (
                                <div key={g.dia} style={{ border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 8, overflow: 'hidden' }}>
                                    <div style={{
                                        display: 'flex', alignItems: 'center', gap: 8, background: '#f8fafc',
                                        padding: '6px 8px 6px 4px', borderBottom: aberta ? '1px solid #e2e8f0' : 'none',
                                    }}>
                                        <button type="button" onClick={() => alternarEntrega(g.dia)}
                                            aria-expanded={aberta} aria-controls={`entrega-epi-${g.dia}`}
                                            title={aberta ? 'Recolher entrega' : 'Abrir entrega'}
                                            style={{
                                                display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0,
                                                padding: '4px 6px', border: 0, background: 'transparent',
                                                color: '#334155', textAlign: 'left', cursor: 'pointer',
                                            }}>
                                            <svg aria-hidden="true" viewBox="0 0 20 20" width="16" height="16"
                                                style={{ flex: '0 0 auto', transform: aberta ? 'rotate(90deg)' : 'none', transition: 'transform 160ms ease' }}>
                                                <path d="M7 4.5 12.5 10 7 15.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                            <strong style={{ fontSize: '0.86rem' }}>{titulo}</strong>
                                            <span style={{ fontSize: '0.8rem', color: '#64748b' }}>
                                                {g.itens.length} EPI{g.itens.length === 1 ? '' : 's'}
                                            </span>
                                        </button>
                                        <button data-liquid-secondary="true" onClick={() => ficha(g.dia === 'sem-data' ? undefined : g.dia)}
                                            style={{ ...btnSec, flex: '0 0 auto', padding: '4px 10px', fontSize: '0.78rem' }}
                                            title="Ficha apenas com os EPIs desta distribuição"><LiquidText>⬇ Ficha desta entrega</LiquidText></button>
                                    </div>
                                    {aberta && (
                                        <table id={`entrega-epi-${g.dia}`} style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                                            <thead>
                                                <tr style={{ textAlign: 'left', color: '#64748b' }}>
                                                    <th style={{ padding: '4px 10px' }}>EPI</th>
                                                    <th style={{ padding: '4px 6px' }}>Tam.</th>
                                                    <th style={{ padding: '4px 6px' }}>Qt.</th>
                                                    <th style={{ padding: '4px 6px' }}>Substituição</th>
                                                    <th style={{ padding: '4px 6px' }}>Obs.</th>
                                                    <th></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {g.itens.map(a => (
                                                    <tr key={a.id} style={{ borderTop: '1px solid #f8fafc' }}>
                                                        <td style={{ padding: '5px 10px' }}><strong>{a.ref}.</strong> {a.designacao}</td>
                                                        <td style={{ padding: '5px 6px' }}>{a.tamanho || '—'}</td>
                                                        <td style={{ padding: '5px 6px' }}>{a.quantidade}</td>
                                                        <td style={{ padding: '5px 6px', whiteSpace: 'nowrap' }}>{fmtData(a.data_substituicao)}</td>
                                                        <td style={{ padding: '5px 6px', color: '#64748b' }}>{a.observacoes || '—'}</td>
                                                        <td style={{ textAlign: 'right', paddingRight: 8 }}>
                                                            <button data-liquid-secondary="true" onClick={() => remover(a)} title="Remover esta linha da entrega"
                                                                style={{ ...btnSec, padding: '2px 8px', fontSize: '0.75rem', color: '#e53e3e' }}><DeleteIcon /></button>
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}

                {/* Nova entrega: marcar só o que se entrega agora — um par de luvas,
                    alguns EPIs ou todos os que faltam. */}
                <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
                        <strong style={{ color: '#334155' }}>Nova entrega</strong>
                        <span style={{ fontSize: '0.8rem', color: '#64748b' }}>Cada entrega fica com a sua data e a sua linha na ficha.</span>
                    </div>

                    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', margin: '8px 0 10px' }}>
                        <div>
                            <label data-liquid-label="true" style={lbl}>Data da entrega</label>
                            <input type="date" value={data} onChange={e => setData(e.target.value)} style={inp} />
                        </div>
                        <div style={{ flex: 1, minWidth: 180 }}>
                            <label data-liquid-label="true" style={lbl}>Observações (opcional)</label>
                            <input value={obs} onChange={e => setObs(e.target.value)} placeholder="ex.: substituição por desgaste" style={{ ...inp, width: '100%' }} />
                        </div>
                        <button data-liquid-secondary="true" onClick={marcarEmFalta} disabled={emFalta.length === 0} style={{ ...btnSec, opacity: emFalta.length ? 1 : 0.5 }}>
                            Marcar os que faltam ({emFalta.length})
                        </button>
                        {nSel > 0 && <button data-liquid-secondary="true" onClick={() => setSel({})} style={btnSec}>Limpar</button>}
                    </div>

                    <div style={{ border: '1px solid #e2e8f0', borderRadius: 8, maxHeight: 250, overflowY: 'auto' }}>
                        {disponiveis.map(c => {
                            const s = sel[c.id];
                            return (
                                <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', borderBottom: '1px solid #f8fafc', background: s ? '#f0f9ff' : '#fff' }}>
                                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0, cursor: 'pointer' }}>
                                        <input type="checkbox" checked={!!s} onChange={() => alternar(c.id)} />
                                        <span style={{ fontSize: '0.86rem', color: '#334155' }}>
                                            <strong>{c.ref}.</strong> {c.designacao}
                                            {jaAtribuidos.has(c.id) && <span style={{ color: '#94a3b8', fontSize: '0.76rem' }}> · já entregue antes</span>}
                                        </span>
                                    </label>
                                    {s && (
                                        <>
                                            <input value={s.tamanho} onChange={e => definir(c.id, 'tamanho', e.target.value)}
                                                placeholder="tam." aria-label={`Tamanho de ${c.designacao}`} style={{ ...inp, width: 68, padding: '4px 8px' }} />
                                            <input type="number" min="1" value={s.quantidade} onChange={e => definir(c.id, 'quantidade', e.target.value)}
                                                aria-label={`Quantidade de ${c.designacao}`} style={{ ...inp, width: 58, padding: '4px 8px' }} />
                                        </>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <div data-liquid-actions="true" style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 10, flexWrap: 'wrap' }}>
                        <button data-liquid-primary="true" onClick={entregar} disabled={aEntregar || nSel === 0} style={{ ...btnPri, opacity: (aEntregar || nSel === 0) ? 0.5 : 1 }}>
                            <LiquidText>{aEntregar ? 'A registar…' : nSel === 0 ? '🧤 Entregar' : `🧤 Entregar ${nSel} EPI${nSel === 1 ? '' : 's'}`}</LiquidText>
                        </button>
                        <button data-liquid-secondary="true" onClick={() => ficha()} style={btnSec}><LiquidText>📄 Ficha de posse (PDF)</LiquidText></button>
                    </div>
                    {err && <div style={{ color: err.startsWith('✓') ? '#166534' : '#e53e3e', marginTop: 8, fontSize: '0.85rem' }}>{err}</div>}
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Catálogo de EPIs ---------- */
function CatalogoModal({ catalogo, onClose, onChanged }) {
    const [novo, setNovo] = useState({ ref: '', designacao: '', riscos: '' });
    const [err, setErr] = useState('');

    const adicionar = async () => {
        if (!novo.designacao.trim()) { setErr('Indique a designação.'); return; }
        setErr('');
        try { await api.post('/sistema-integrado/epi-catalogo', novo); setNovo({ ref: '', designacao: '', riscos: '' }); onChanged(); }
        catch (e) { setErr(e.response?.data?.error || 'Erro'); }
    };
    const remover = async (c) => {
        if (!window.confirm(`Eliminar "${c.designacao}" do catálogo? (remove-o de todos os trabalhadores)`)) return;
        try { await api.delete(`/sistema-integrado/epi-catalogo/${c.id}`); onChanged(); }
        catch { alert('Erro ao eliminar'); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 560 }} onClick={e => e.stopPropagation()}>
                <div data-sgi-dialog-heading="true" style={modalHead}>
                    <h3 style={{ margin: 0 }}>Catálogo de EPIs</h3>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.86rem', marginBottom: 14 }}>
                    <thead>
                        <tr style={{ textAlign: 'left', color: '#64748b', borderBottom: '1px solid #e2e8f0' }}>
                            <th style={{ padding: '4px 6px', width: 40 }}>Ref</th>
                            <th style={{ padding: '4px 6px' }}>Designação</th>
                            <th style={{ padding: '4px 6px' }}>Riscos</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {catalogo.map(c => (
                            <tr key={c.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                                <td style={{ padding: '4px 6px', fontWeight: 700 }}>{c.ref}</td>
                                <td style={{ padding: '4px 6px' }}>{c.designacao}</td>
                                <td style={{ padding: '4px 6px', color: '#64748b' }}>{c.riscos || '—'}</td>
                                <td style={{ textAlign: 'right' }}>
                                    <button data-liquid-secondary="true" onClick={() => remover(c)} style={{ ...btnSec, padding: '2px 8px', fontSize: '0.75rem', color: '#e53e3e' }} aria-label="Apagar" title="Apagar"><DeleteIcon /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap', borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Ref</label>
                        <input value={novo.ref} onChange={e => setNovo(p => ({ ...p, ref: e.target.value }))} style={{ ...inp, width: 54 }} />
                    </div>
                    <div style={{ flex: 1, minWidth: 160 }}>
                        <label data-liquid-label="true" style={lbl}>Designação</label>
                        <input value={novo.designacao} onChange={e => setNovo(p => ({ ...p, designacao: e.target.value }))} style={{ ...inp, width: '100%' }} />
                    </div>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Riscos</label>
                        <input value={novo.riscos} onChange={e => setNovo(p => ({ ...p, riscos: e.target.value }))} placeholder="ex.: 3; 11" style={{ ...inp, width: 90 }} />
                    </div>
                    <button data-liquid-primary="true" onClick={adicionar} style={btnPri}>+ Adicionar</button>
                </div>
                {err && <div style={{ color: '#e53e3e', marginTop: 8, fontSize: '0.85rem' }}>{err}</div>}
            </div>
        </LiquidDialog>
    );
}

/* ---------- Componente principal ---------- */
const Epis = () => {
    const [resumo, setResumo] = useState([]);
    const [catalogo, setCatalogo] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pesquisa, setPesquisa] = useState('');
    const [trabalhador, setTrabalhador] = useState(null);
    const [verCatalogo, setVerCatalogo] = useState(false);
    const [dataDist, setDataDist] = useState(new Date().toISOString().substring(0, 10));
    const [distribuindo, setDistribuindo] = useState(false);
    const [aBaixar, setABaixar] = useState(null);

    const baixarFicha = async (x) => {
        setABaixar(x.trabalhador_id);
        try { await descarregarFicha(x.trabalhador_id, x.nome); }
        catch { alert('Não foi possível gerar a ficha deste trabalhador.'); }
        finally { setABaixar(null); }
    };

    const carregar = useCallback(async () => {
        setLoading(true);
        try {
            const [r, c] = await Promise.all([
                api.get('/sistema-integrado/epi-atribuicoes', { params: { resumo: 1 } }),
                api.get('/sistema-integrado/epi-catalogo'),
            ]);
            setResumo(r.data || []);
            setCatalogo(c.data || []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { carregar(); }, [carregar]);

    let visiveis = resumo;
    if (pesquisa.trim()) {
        const t = pesquisa.trim().toLowerCase();
        visiveis = visiveis.filter(x => (x.nome || '').toLowerCase().includes(t) || (x.categoria_profissional || '').toLowerCase().includes(t));
    }
    const semEpis = resumo.filter(x => !x.n_epis).length;
    const nCatalogo = catalogo.filter(c => c.ativo !== false).length;
    const incompletos = resumo.filter(x => (x.n_epis || 0) < nCatalogo).length;

    const distribuirATodos = async () => {
        if (incompletos === 0) { alert('Todos os trabalhadores já têm o catálogo completo de EPIs.'); return; }
        if (!window.confirm(`Distribuir os EPIs em falta a ${incompletos} trabalhador(es), com entrega em ${new Date(dataDist).toLocaleDateString('pt-PT')}?`)) return;
        setDistribuindo(true);
        try {
            const ids = resumo.map(x => x.trabalhador_id);
            const r = await api.post('/sistema-integrado/epi-atribuicoes/todos', { trabalhador_ids: ids, data_entrega: dataDist });
            carregar();
            alert(r.data.message || 'Distribuído.');
        } catch (e) { alert(e.response?.data?.error || 'Erro ao distribuir.'); }
        finally { setDistribuindo(false); }
    };

    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10, marginBottom: 14 }}>
                <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Pesquisar trabalhador</label>
                        <input value={pesquisa} onChange={e => setPesquisa(e.target.value)} placeholder="Nome, categoria…" style={{ ...inp, width: 220 }} />
                    </div>
                    {semEpis > 0 && <span data-liquid-tone={liquidTone('#fef9c3')} data-liquid-badge="true" style={{ ...badge, background: '#fef9c3', color: '#854d0e', marginBottom: 6 }}>{semEpis} sem EPIs registados</span>}
                </div>
                <div data-liquid-actions="true" style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Data de entrega</label>
                        <input type="date" value={dataDist} onChange={e => setDataDist(e.target.value)} style={inp} />
                    </div>
                    <button data-liquid-primary="true" onClick={distribuirATodos} disabled={distribuindo || incompletos === 0} style={{ ...btnPri, opacity: (distribuindo || incompletos === 0) ? 0.5 : 1 }}
                        title="Distribuir os EPIs do catálogo em falta a todos os trabalhadores, na data indicada">
                        <LiquidText>{distribuindo ? 'A distribuir…' : `🧤 Distribuir a todos (${incompletos})`}</LiquidText>
                    </button>
                    <button data-liquid-secondary="true" onClick={() => setVerCatalogo(true)} style={btnSec}><LiquidText>📋 Catálogo (</LiquidText>{catalogo.length})</button>
                </div>
            </div>

            {loading ? <p style={{ color: '#718096' }}>A carregar…</p>
                : visiveis.length === 0 ? (
                    <div style={{ padding: '48px 0', textAlign: 'center', color: '#94a3b8' }}>Nenhum trabalhador encontrado.</div>
                ) : (
                    <div className="table-responsive" style={{ backgroundColor: 'white', borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'auto' }}>
                        <table className="standard-table">
                            <thead>
                                <tr>
                                    <th>Trabalhador</th>
                                    <th>Categoria</th>
                                    <th style={{ textAlign: 'center' }}>EPIs registados</th>
                                    <th>Última entrega</th>
                                    <th style={{ textAlign: 'center' }}>Ficha assinada</th>
                                    <th style={{ textAlign: 'right' }}>Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {visiveis.map(x => (
                                    <tr key={x.trabalhador_id}>
                                        <td style={{ fontWeight: 600 }}>{x.nome}</td>
                                        <td>{x.categoria_profissional || '—'}</td>
                                        <td style={{ textAlign: 'center' }}>
                                            <span data-liquid-tone={liquidTone(x.n_epis ? '#dcfce7' : '#fee2e2')} data-liquid-badge="true" style={{ ...badge, background: x.n_epis ? '#dcfce7' : '#fee2e2', color: x.n_epis ? '#166534' : '#991b1b' }}>{x.n_epis}</span>
                                        </td>
                                        <td style={{ whiteSpace: 'nowrap' }}>{fmtData(x.ultima_entrega)}</td>
                                        {/* A distribuição mais recente, igual ao botão 🛡️ do trabalhador */}
                                        <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                                            <button data-liquid-secondary="true" onClick={() => baixarFicha(x)} disabled={!x.n_epis || aBaixar === x.trabalhador_id}
                                                style={{ ...btnSec, ...mini, marginLeft: 0, opacity: x.n_epis ? 1 : 0.45, cursor: x.n_epis ? 'pointer' : 'not-allowed' }}
                                                title={x.n_epis
                                                    ? `Ficha da distribuição mais recente, de ${fmtData(x.ultima_entrega)}`
                                                    : 'Sem entregas registadas — a ficha sairia em branco'}>
                                                <LiquidText>{aBaixar === x.trabalhador_id ? '…' : '⬇ PDF'}</LiquidText>
                                            </button>
                                        </td>
                                        <td style={{ textAlign: 'right' }}>
                                            <button data-liquid-primary="true" onClick={() => setTrabalhador(x)} style={{ ...btnPri, ...mini }}>Gerir EPIs</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

            {trabalhador && <TrabalhadorModal trabalhador={trabalhador} catalogo={catalogo} onClose={() => setTrabalhador(null)} onChanged={carregar} />}
            {verCatalogo && <CatalogoModal catalogo={catalogo} onClose={() => setVerCatalogo(false)} onChanged={carregar} />}
        </div>
    );
};

/* ---------- estilos ---------- */
const lbl = { display: 'block', fontSize: '0.74rem', color: '#64748b', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.03em' };
const inp = { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.9rem', boxSizing: 'border-box' };
const badge = { padding: '3px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' };
const btnSec = { padding: '7px 14px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', color: '#334155' };
const btnPri = { padding: '7px 14px', border: '1px solid #3182ce', background: '#3182ce', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem' };
const mini = { padding: '4px 9px', marginLeft: 6, fontSize: '0.76rem' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '40px 16px', overflowY: 'auto' };
const modal = { background: '#fff', borderRadius: 14, padding: 22, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' };
const modalHead = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 };
const closeBtn = { border: 'none', background: 'transparent', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b' };

export default Epis;
